// Active-role and delegation persistence, shared (#317 A70).
//
// Moved here from backoffice-ui, which was the ONLY app that stamped these keys.
// time-ui, people-ui and apprentice-ui read and write the same
// `activeRoleId:{slug}` / `kb_task_acting:{slug}` keys on the same origin —
// the whole suite for a tenant is one host, modules by path (docs/operations/environments.md)
// — so localStorage is shared between them. Without the stamp, signing out of one
// account and into another in the same browser leaves the first user's role id in
// place, and the sibling apps put it on the wire: ats-api answers
// ACTIVE_ROLE_NOT_ASSIGNED and those first requests are never retried.
//
// app-kit rather than a fourth copy (ADR 0026): the stamp is a security rule, and
// a rule enforced by one of four copies is not enforced.

// Persistence of the chosen active role (the `X-Active-Role-Id` header) and of the
// task delegation target (`X-Act-As-Tenant-User-Id`).
//
// Both values are keyed per tenant AND stamped with the master user who chose them.
// Keyed per tenant alone — as they were until issue 264 — they outlive the session
// that wrote them: sign out of one account and into another in the same browser and
// the keys still hold the first user's role/delegation, so the new session's first
// requests go out carrying context that user does not hold and ats-api rejects them
// with `ACTIVE_ROLE_NOT_ASSIGNED` (or a delegation error). Those first requests are
// never retried.
//
// The key NAMES stay `activeRoleId:{slug}` / `kb_task_acting:{slug}` deliberately:
// people-ui, time-ui and apprentice-ui read them, so renaming them here would
// silently drop their headers instead of fixing anything. The stamps live in
// separate `*Owner:` keys the siblings never look at.
//
// The whole suite for a tenant is one origin (`{tenant}.lumi-hr.com`, modules by
// path — docs/operations/environments.md), so localStorage is shared across the module UIs and
// apprentice-ui writes `activeRoleId:{slug}` itself, unstamped. That drives two
// rules below:
//   - the stamp records the role id it vouches for, not just the owner, so a
//     sibling overwriting the value invalidates the stamp instead of inheriting it;
//   - a read deletes only what it can account for. A value stamped by another
//     account is our own stale write and goes. A value the stamp no longer
//     describes was overwritten by a sibling and stays, even though we refuse to
//     send it — failing closed means not sending it, not destroying someone
//     else's live choice.

export function safeGetItem(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

export function safeSetItem(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    // ignore storage access errors
  }
}

export function safeRemoveItem(key: string) {
  try {
    localStorage.removeItem(key);
  } catch {
    // ignore storage access errors
  }
}

const ROLE_PREFIX = 'activeRoleId:';
const ROLE_OWNER_PREFIX = 'activeRoleOwner:';

// The module whose role ids this app sends (#317 A62).
//
// A role id is MODULE-SCOPED — an `ats_admin` id means nothing in apprentice-api's
// catalogue — but the key was not, so all five module UIs on the tenant origin
// shared one slot. Whichever app the user last switched roles in decided what
// every other app found there. The owner stamp (#317 A70) made that safe rather
// than correct: a sibling's overwrite invalidates the stamp, so the other apps
// stop sending the header and fail closed, and the user has to re-pick a role they
// never changed.
//
// Scoping the key by module removes the collision instead of surviving it. The
// scope is the API the header is FOR, not the app that sends it — backoffice-ui
// and workspace-ui both talk to ats-api and must share one choice.
//
// Unset is the pre-#317 key, so an app that never calls the setter keeps the old
// behaviour rather than silently reading an empty slot.
let roleModuleKey = '';

/** Declare which module's role ids this app persists. Call once, at startup. */
export function configureActiveRoleModule(moduleKey: string): void {
  roleModuleKey = (moduleKey || '').trim();
}

// A caller may scope one read/write to a specific module instead of the
// app-global key — workspace-ui talks to several module APIs from ONE app
// (ADR 0062 hub) and each client must read the slot of the API it calls.
const roleSuffix = (tenantSlug: string, moduleOverride?: string) => {
  const module = (moduleOverride ?? roleModuleKey).trim();
  return module ? `${tenantSlug}:${module}` : tenantSlug;
};
const ACTING_PREFIX = 'kb_task_acting:';
const ACTING_OWNER_PREFIX = 'kb_task_actingOwner:';

// userIds are UUIDs and role/tenant-user ids are cuids, so neither contains `|`.
const STAMP_SEPARATOR = '|';

function stamp(userId: string, value: string) {
  return `${userId}${STAMP_SEPARATOR}${value}`;
}

/**
 * The stored value, if the stamp vouches that THIS user chose exactly THIS value.
 * Deletes only what it can account for: a value another account stamped is our own
 * stale write. Anything else is left in place — a sibling module UI may own it.
 */
export function readStamped(
  valueKey: string,
  ownerKey: string,
  userId: string | null | undefined,
): string | null {
  const value = safeGetItem(valueKey);
  if (!value) return null;
  // Without a signed-in user there is nobody to attribute the choice to.
  if (!userId) return null;
  const stamped = safeGetItem(ownerKey);
  if (!stamped) return null;

  const sep = stamped.indexOf(STAMP_SEPARATOR);
  const stampedBy = sep === -1 ? null : stamped.slice(0, sep);
  const stampedValue = sep === -1 ? null : stamped.slice(sep + 1);

  if (stampedBy === userId && stampedValue === value) return value;

  if (stampedBy !== userId && stampedValue === value) {
    // Our own write, for the account that used this browser before. The value is
    // that user's, and a sibling module that does not check stamps would put it on
    // the wire for this one — so it goes, together with the stamp.
    safeRemoveItem(valueKey);
    safeRemoveItem(ownerKey);
    return null;
  }

  // The stamp no longer describes what is stored: a sibling overwrote the value
  // after we stamped it. Whoever it belongs to now, it is not ours to delete — it
  // may be their live choice, for this very user. Fail closed by not sending it.
  return null;
}

export function writeStamped(valueKey: string, ownerKey: string, userId: string, value: string) {
  safeSetItem(valueKey, value);
  safeSetItem(ownerKey, stamp(userId, value));
}

/**
 * The role id this user chose for this tenant, or null if there isn't one we can
 * vouch for.
 */
export function readActiveRoleId(
  tenantSlug: string | null | undefined,
  userId: string | null | undefined,
  module?: string,
): string | null {
  if (!tenantSlug) return null;
  const suffix = roleSuffix(tenantSlug, module);
  return readStamped(`${ROLE_PREFIX}${suffix}`, `${ROLE_OWNER_PREFIX}${suffix}`, userId);
}

export function writeActiveRoleId(
  tenantSlug: string | null | undefined,
  userId: string | null | undefined,
  roleId: string,
  module?: string,
): void {
  if (!tenantSlug || !userId) return;
  const suffix = roleSuffix(tenantSlug, module);
  writeStamped(`${ROLE_PREFIX}${suffix}`, `${ROLE_OWNER_PREFIX}${suffix}`, userId, roleId);
}

export function clearActiveRoleId(tenantSlug: string | null | undefined, module?: string): void {
  if (!tenantSlug) return;
  const suffix = roleSuffix(tenantSlug, module);
  safeRemoveItem(`${ROLE_PREFIX}${suffix}`);
  safeRemoveItem(`${ROLE_OWNER_PREFIX}${suffix}`);
}

/**
 * The tenant user this user is acting as (task delegation), or null. Same ownership
 * rules as the role id: the workspace hands off to backoffice with `?token=` and no
 * logout in between, so an unvouched delegation must not reach ats-api.
 */
export function readActingUserId(
  tenantSlug: string | null | undefined,
  userId: string | null | undefined,
): string | null {
  if (!tenantSlug) return null;
  return readStamped(`${ACTING_PREFIX}${tenantSlug}`, `${ACTING_OWNER_PREFIX}${tenantSlug}`, userId);
}

export function writeActingUserId(
  tenantSlug: string | null | undefined,
  userId: string | null | undefined,
  tenantUserId: string,
): void {
  if (!tenantSlug || !userId) return;
  writeStamped(
    `${ACTING_PREFIX}${tenantSlug}`,
    `${ACTING_OWNER_PREFIX}${tenantSlug}`,
    userId,
    tenantUserId,
  );
}

export function clearActingUserId(tenantSlug: string | null | undefined): void {
  if (!tenantSlug) return;
  safeRemoveItem(`${ACTING_PREFIX}${tenantSlug}`);
  safeRemoveItem(`${ACTING_OWNER_PREFIX}${tenantSlug}`);
}

// The demo persona (#409) is the third owner-stamped, per-tenant choice on this
// origin; its readers and writers live in demoPersona.ts, but it is cleared HERE
// with the other two so a sign-out cannot leave a mask behind for the next
// account. Same key convention, same reason.
export const DEMO_PERSONA_PREFIX = 'demoPersona:';
export const DEMO_PERSONA_OWNER_PREFIX = 'demoPersonaOwner:';

/**
 * Drop every tenant's role choice, task-delegation and demo-persona state. Called
 * on logout so the next account in this browser cannot inherit any of them.
 */
export function clearAllActiveRoleState(): void {
  let keys: string[] = [];
  try {
    keys = Object.keys(localStorage);
  } catch {
    return;
  }
  const prefixes = [ROLE_PREFIX, ROLE_OWNER_PREFIX, ACTING_PREFIX, ACTING_OWNER_PREFIX, DEMO_PERSONA_PREFIX, DEMO_PERSONA_OWNER_PREFIX];
  for (const key of keys) {
    if (prefixes.some((p) => key.startsWith(p))) {
      safeRemoveItem(key);
    }
  }
}

/**
 * Drop one workspace's role choice, task delegation and demo persona — every
 * module's slot, not just this app's.
 *
 * `clearActiveRoleId` clears the slot of ONE module (ADR 0062 scopes the key by
 * the API the header is for), and `clearAllActiveRoleState` clears every
 * workspace. Neither is what an erased workspace needs: its ids are gone from
 * every module at once, and the workspaces beside it in this browser are
 * untouched. So this one matches on the slug and takes whatever suffix follows.
 */
export function clearWorkspaceRoleState(tenantSlug: string | null | undefined): void {
  const slug = String(tenantSlug || '').trim();
  if (!slug) return;
  let keys: string[] = [];
  try {
    keys = Object.keys(localStorage);
  } catch {
    return;
  }
  const prefixes = [
    ROLE_PREFIX,
    ROLE_OWNER_PREFIX,
    ACTING_PREFIX,
    ACTING_OWNER_PREFIX,
    DEMO_PERSONA_PREFIX,
    DEMO_PERSONA_OWNER_PREFIX,
  ];
  for (const key of keys) {
    // `<prefix><slug>` exactly, or `<prefix><slug>:<module>` — never a slug this
    // one is merely a prefix of ("acme" must not reach "acme-gmbh").
    if (prefixes.some((p) => key === `${p}${slug}` || key.startsWith(`${p}${slug}:`))) {
      safeRemoveItem(key);
    }
  }
}

/** The user a stamp (`{userId}|{value}`) vouches for, or null when there is none. */
function stampedBy(ownerKey: string): string | null {
  const stamped = safeGetItem(ownerKey);
  if (!stamped) return null;
  const sep = stamped.indexOf(STAMP_SEPARATOR);
  return sep === -1 ? null : stamped.slice(0, sep);
}

/**
 * Remove a stamped value and its stamp, but only when the stamp says THIS user
 * wrote it. A value another account stamped, or one with no stamp to account for
 * it, is not this user's to delete and stays.
 */
export function clearStampedForOwner(valueKey: string, ownerKey: string, userId: string | null | undefined): void {
  if (!userId || stampedBy(ownerKey) !== userId) return;
  safeRemoveItem(valueKey);
  safeRemoveItem(ownerKey);
}

/**
 * `clearWorkspaceRoleState`, for ONE account: drop only the role choice,
 * delegation and demo persona that `userId` stamped for this workspace (every
 * module's slot). Another account that used this browser keeps its own — for a
 * member who lost access to a workspace the others did not.
 */
export function clearWorkspaceRoleStateForOwner(
  tenantSlug: string | null | undefined,
  userId: string | null | undefined,
): void {
  const slug = String(tenantSlug || '').trim();
  if (!slug || !userId) return;
  let keys: string[] = [];
  try {
    keys = Object.keys(localStorage);
  } catch {
    return;
  }
  const pairs: Array<[string, string]> = [
    [ROLE_PREFIX, ROLE_OWNER_PREFIX],
    [ACTING_PREFIX, ACTING_OWNER_PREFIX],
    [DEMO_PERSONA_PREFIX, DEMO_PERSONA_OWNER_PREFIX],
  ];
  for (const key of keys) {
    for (const [valuePrefix, ownerPrefix] of pairs) {
      if (key === `${valuePrefix}${slug}` || key.startsWith(`${valuePrefix}${slug}:`)) {
        clearStampedForOwner(key, `${ownerPrefix}${key.slice(valuePrefix.length)}`, userId);
      }
    }
  }
}

/**
 * One sign-out clear for the whole tenant-origin session (issue 195, D16). The
 * module UIs share one origin, so a sign-out anywhere has to drop every
 * tenant-scoped key on it — not just the app's own session token — or the next
 * account signed into this browser inherits them (the same class of bug #317 A70
 * fixed for the active-role stamp).
 *
 * `ownKeys` is the caller's own session keys (its token, refresh token,
 * tenant-slug/id — whatever it persists under its own names, which differ per
 * app: backoffice-ui's `token`/`tenantSlug`, workspace-ui's `token`/
 * `refreshToken`, …). This function does not guess those names; it clears
 * exactly what it is handed, plus every tenant's active-role and task-delegation
 * state via `clearAllActiveRoleState` — the part that is identical everywhere
 * because the storage keys ARE shared across the module UIs.
 */
export function clearTenantSessionState(ownKeys: readonly string[] = []): void {
  for (const key of ownKeys) {
    safeRemoveItem(key);
  }
  clearAllActiveRoleState();
}

/**
 * Switch the active role and stay where the person is (#429).
 *
 * The write comes first and is what survives the navigation: the id is stored per
 * (workspace, user) in this origin's localStorage, and every request interceptor
 * reads it from THERE, not from a store — so the next page load is already the
 * new role.
 *
 * The navigation is a reload of the current URL. It used to be a bounce to the
 * workspace home, because a plain reload "picked up the new header on a page the
 * new role may not be allowed to see" — but that answered a permission question
 * by throwing away the person's place in the product for every switch, including
 * the overwhelming majority where the page is fine. The router guard is what
 * knows: each module's guard already sends a refused route to its own landing
 * page, so a reload lands on the same page when the new role may see it and on
 * the module's dashboard when it may not.
 *
 * A reload, not a router push: the active role travels as a REQUEST HEADER, so
 * every view's already-fetched data was read as the previous role. Re-mounting
 * the app is what makes the whole page agree with the header.
 *
 * Returns false without touching anything when there is no workspace to scope the
 * write to — the caller then has nothing to do.
 */
export function switchActiveRole(
  tenantSlug: string | null | undefined,
  userId: string | null | undefined,
  roleId: string,
  /** Land here instead of reloading the current URL — a same-origin path
   * (#1028: a greyed sidebar entry's "Open as X" opens that entry). Anything
   * that is not a same-origin path is ignored and the page reloads as before. */
  targetPath?: string,
): boolean {
  const slug = String(tenantSlug || '').trim();
  const id = String(roleId || '').trim();
  if (!slug || !id) return false;
  writeActiveRoleId(slug, userId, id);
  if (typeof window === 'undefined') return true;
  const target = sameOriginTarget(targetPath);
  if (target) window.location.assign(target);
  else window.location.reload();
  return true;
}

// A target is followed only when it resolves to THIS origin. Parsed rather than
// prefix-checked: `/\\host` and the like start with one slash yet leave the site.
function sameOriginTarget(targetPath: string | undefined): string | null {
  const raw = String(targetPath || '').trim();
  if (!raw.startsWith('/')) return null;
  try {
    const url = new URL(raw, window.location.href);
    return url.origin === window.location.origin ? `${url.pathname}${url.search}${url.hash}` : null;
  } catch {
    return null;
  }
}
